import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ChargePointWrapper,
  ChargePointCluster,
  PinColor,
} from '@/features/charge-points/types/charge-point.types';
import { StateBreakdownTooltip } from './StateBreakdownTooltip';
import { useOperatorStore } from '@/features/operator/store/operator-store';
import { useFilterStore } from '@/features/filters/store/filter-store';
import { getContrastingTextColor } from '@/utils/color-utils';
import {
  getCustomBorderColor,
  countColorsTotal,
  colorsFromPinColor,
  getPillSizeClass,
} from '../utils/marker-style';
import { cn } from '@/utils/cn';
import { maxKwFromLabel } from '@/features/charge-points/utils/nearby';
import { useMarkerCustomization } from '../hooks/useMarkerCustomization';

interface ChargePointMarkerProps {
  chargePoint: ChargePointWrapper;
  isOpen: boolean;
  showOperatorIcon?: boolean;
  highlightColor?: string;
  highlightIconUrl?: string;
  overlapCount?: number;
  overlapGroup?: (ChargePointWrapper | ChargePointCluster)[];
}

// Default status colors follow the theme tokens (brighter in dark mode)
const THEMED_STATUS_COLOR: Record<string, string> = {
  green: 'rgb(var(--c-ok))',
  orange: 'rgb(var(--c-warn))',
  red: 'rgb(239 68 68)',
  gray: 'rgb(var(--c-muted))',
};

/** Max power of a charge point or site (clusters carry no power) */
function kwOf(
  item: ChargePointWrapper | ChargePointCluster
): number | undefined {
  if ('count' in item) return undefined;
  return item.type === 'chargePointGroup'
    ? maxKwFromLabel(item.chargePointGroup?.maxKwLabel)
    : item.chargePoint?.maxKw;
}

/**
 * Individual charge point marker
 */
export function ChargePointMarker({
  chargePoint,
  isOpen,
  showOperatorIcon = false,
  highlightColor = '#302ee0', // Default to Monta blue
  highlightIconUrl,
  overlapCount,
  overlapGroup,
}: ChargePointMarkerProps) {
  const { t } = useTranslation();
  const { pin } = chargePoint;
  const isGroup = pin.type === 'group';
  const [isHovered, setIsHovered] = useState(false);
  const hasOverlaps = overlapCount && overlapCount > 1;

  // Get operator theme and marker style
  const operatorTheme = useOperatorStore((s) => s.theme);
  const markerStyle = useFilterStore((s) => s.markerStyle);

  // Marker customization from marker_* URL params (no-op when unset)
  const { statusColors, markerColor, markerSize } = useMarkerCustomization();

  // Calculate inner circle styles based on marker style
  const innerCircleStyle = useMemo(() => {
    // Top priority: explicit marker_color accent (overrides marker_style center)
    if (markerColor) {
      return {
        backgroundColor: markerColor,
        color: getContrastingTextColor(markerColor),
      };
    }
    if (markerStyle === 'operator' && operatorTheme?.primary_color) {
      const bgColor = operatorTheme.primary_color;
      const textColor = getContrastingTextColor(bgColor);
      return {
        backgroundColor: bgColor,
        color: textColor,
      };
    }
    if (markerStyle === 'operator_neutral') {
      // Beige/warm neutral background
      return {
        backgroundColor: '#F5F0E8',
        color: 'rgb(17, 24, 39)',
      };
    }
    // Default style: theme surface + ink (white/dark in light, dark/white in dark)
    return {
      backgroundColor: 'rgb(var(--c-surface))',
      color: 'rgb(var(--c-ink))',
    };
  }, [markerColor, markerStyle, operatorTheme]);

  // Calculate combined colors and count from all overlapping markers
  const combinedData = useMemo(() => {
    if (!hasOverlaps || !overlapGroup || overlapGroup.length === 0) {
      // No overlaps, use single marker data
      const getTotalFromColors = () => {
        if (!pin.colors) return pin.number;
        const green = pin.colors.green || 0;
        const grey = (pin.colors.grey || 0) + (pin.colors.gray || 0);
        const orange = pin.colors.orange || 0;
        const red = pin.colors.red || 0;
        const blue = pin.colors.blue || 0;
        return green + grey + orange + red + blue;
      };

      // Use the per-status breakdown when present, otherwise derive a
      // single-color breakdown from pin.color so colorless / zero-count groups
      // still render their status color (and any custom colors).
      const singleColors =
        countColorsTotal(pin.colors) > 0
          ? pin.colors
          : colorsFromPinColor(pin.color);

      return {
        colors: singleColors,
        totalCount: isGroup ? getTotalFromColors() : 1,
      };
    }

    // Calculate combined data from all overlapping markers
    let totalGreen = 0;
    let totalGrey = 0;
    let totalOrange = 0;
    let totalRed = 0;
    let totalBlue = 0;
    let totalCount = 0;

    overlapGroup.forEach((item) => {
      if ('count' in item) {
        // It's a cluster
        totalCount += item.count;
        if (item.pin?.colors) {
          totalGreen += item.pin.colors.green || 0;
          totalGrey +=
            (item.pin.colors.grey || 0) + (item.pin.colors.gray || 0);
          totalOrange += item.pin.colors.orange || 0;
          totalRed += item.pin.colors.red || 0;
          totalBlue += item.pin.colors.blue || 0;
        }
      } else {
        // It's a charge point wrapper
        const cpPin = item.pin;
        if (item.type === 'chargePointGroup' && cpPin.colors) {
          const cpTotal =
            (cpPin.colors.green || 0) +
            ((cpPin.colors.grey || 0) + (cpPin.colors.gray || 0)) +
            (cpPin.colors.orange || 0) +
            (cpPin.colors.red || 0) +
            (cpPin.colors.blue || 0);
          totalCount += cpTotal;
          totalGreen += cpPin.colors.green || 0;
          totalGrey += (cpPin.colors.grey || 0) + (cpPin.colors.gray || 0);
          totalOrange += cpPin.colors.orange || 0;
          totalRed += cpPin.colors.red || 0;
          totalBlue += cpPin.colors.blue || 0;
        } else {
          totalCount += 1;
          // Single charge point - add to appropriate color based on pin color
          if (cpPin.color === 'green') totalGreen += 1;
          else if (cpPin.color === 'orange') totalOrange += 1;
          else if (cpPin.color === 'red') totalRed += 1;
          else totalGrey += 1;
        }
      }
    });

    return {
      colors: {
        green: totalGreen,
        grey: totalGrey,
        orange: totalOrange,
        red: totalRed,
        blue: totalBlue,
      },
      totalCount,
    };
  }, [hasOverlaps, overlapGroup, pin, isGroup]);

  const displayCount = combinedData.totalCount;

  // No marker_color / marker_style customization: pins use the theme surface
  // and the brand gradient when selected
  const isDefaultStyle =
    !markerColor &&
    markerStyle !== 'operator_neutral' &&
    !(markerStyle === 'operator' && operatorTheme?.primary_color);

  // Sites and stacks show free/total and take the "best" status in the group
  const showCount = Boolean(isGroup || hasOverlaps);
  const colors = combinedData.colors ?? {};
  const available = colors.green ?? 0;
  const groupColor: PinColor =
    available > 0
      ? 'green'
      : (colors.orange ?? 0) > 0
        ? 'orange'
        : (colors.red ?? 0) > 0
          ? 'red'
          : 'gray';
  const statusPinColor = showCount ? groupColor : pin.color;
  const statusColor =
    getCustomBorderColor(statusPinColor, statusColors) ??
    THEMED_STATUS_COLOR[statusPinColor] ??
    THEMED_STATUS_COLOR.gray;

  // Highest power across everything at this spot
  const items =
    hasOverlaps && overlapGroup?.length ? overlapGroup : [chargePoint];
  const kws = items
    .map(kwOf)
    .filter((kw): kw is number => typeof kw === 'number' && kw > 0);
  const label = kws.length ? `${Math.max(...kws)} kW` : null;
  const onAccent = isOpen && isDefaultStyle;

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Aggregated status breakdown on hover for sites and stacks */}
      {isHovered && showCount && (
        <StateBreakdownTooltip
          colors={combinedData.colors}
          totalCount={combinedData.totalCount}
        />
      )}

      {/* Several sites at this spot: draw the pill as a stack of cards */}
      {hasOverlaps && (
        <>
          <span
            aria-hidden
            className="absolute inset-0 translate-x-[8px] -translate-y-[7px] rounded-full bg-surface opacity-60 ring-1 ring-ink/15 shadow-monta-sm"
          />
          <span
            aria-hidden
            className="absolute inset-0 translate-x-[4px] -translate-y-[3.5px] rounded-full bg-surface opacity-85 ring-1 ring-ink/15 shadow-monta-sm"
          />
        </>
      )}

      {/* Selected: slow halo pulse (off for prefers-reduced-motion) */}
      {onAccent && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-full bg-monta-blue motion-safe:animate-pin-halo"
        />
      )}

      <div
        className={cn(
          'relative flex items-center rounded-full font-semibold tabular-nums whitespace-nowrap cursor-pointer transition-transform duration-150',
          getPillSizeClass(markerSize),
          isDefaultStyle &&
            !isOpen &&
            'bg-surface text-ink ring-1 ring-ink/10 shadow-monta-md hover:scale-105',
          onAccent &&
            'bg-monta-blue scale-110 text-white ring-2 ring-white shadow-monta-lg',
          !isDefaultStyle && 'shadow-monta-md hover:scale-105',
          !isDefaultStyle && isOpen && 'scale-110 ring-[3px] ring-ink'
        )}
        style={isDefaultStyle ? undefined : innerCircleStyle}
        aria-label={[
          hasOverlaps
            ? `${overlapCount} charge point sites, ${available} of ${displayCount} available`
            : isGroup
              ? `Charge point site, ${available} of ${displayCount} available`
              : 'Charge point',
          label,
        ]
          .filter(Boolean)
          .join(', ')}
        role="button"
        tabIndex={0}
      >
        <span
          className="h-2 w-2 shrink-0 rounded-full bg-current dark:shadow-[0_0_10px_currentColor]"
          style={{ color: onAccent ? '#FFFFFF' : statusColor }}
        />
        {label && <span>{label}</span>}
        {showCount && (
          <span
            className="text-[0.85em]"
            style={{ color: onAccent ? '#FFFFFF' : statusColor }}
          >
            {t('nearby.freeCount', { count: available })}
          </span>
        )}
      </div>

      {/* Operator icon badge (top-right corner) */}
      {showOperatorIcon && highlightIconUrl && (
        <img
          src={highlightIconUrl}
          alt="Operator icon"
          className="absolute -right-1.5 -top-2 h-4 w-4 rounded-md bg-white object-contain p-0.5 shadow"
          style={
            markerStyle === 'operator_neutral'
              ? undefined
              : { boxShadow: `0 0 0 1.5px ${highlightColor}` }
          }
        />
      )}
    </div>
  );
}
